import type { CmmdForgeAutomationRunDetail } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { sanitizePublicUiError } from "~/productSafeLabels";
import type { ForgeAutomationRunAction } from "./ForgeAutomationRunActions";

const ACTIVE_RUN_STATUSES: ReadonlySet<CmmdForgeAutomationRunDetail["run"]["status"]> = new Set([
  "queued",
  "running",
  "needs_review",
]);

function actionReason(action: ForgeAutomationRunAction): string {
  if (action === "retry") return "Retry requested from Forge run supervision.";
  if (action === "cancel") return "Cancellation requested from Forge run supervision.";
  if (action === "approve") return "Escalation approved from Forge run supervision.";
  return "Escalation blocked from Forge run supervision.";
}

export function useForgeAutomationRunDetail(
  runId: number,
  organizationId: string,
  onChanged: () => void,
) {
  const [detail, setDetail] = useState<CmmdForgeAutomationRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<ForgeAutomationRunAction | null>(null);
  const requestSequence = useRef(0);

  // Stable identity is required because this function owns the active-run polling interval.
  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const nextDetail = await cmmdApiClient.getForgeAutomationRun(runId, { organizationId });
      if (sequence !== requestSequence.current) return;
      setDetail(nextDetail);
      setError(null);
    } catch (cause: unknown) {
      if (sequence !== requestSequence.current) return;
      setError(sanitizePublicUiError(cause, "Unable to load this automation run."));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [organizationId, runId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!detail || !ACTIVE_RUN_STATUSES.has(detail.run.status)) return;
    const timer = window.setInterval(() => void refresh(), 5_000);
    return () => window.clearInterval(timer);
  }, [detail, refresh]);

  async function perform(action: ForgeAutomationRunAction) {
    if (!detail || busyAction !== null) return;
    const authorityKey = `forge-ui:${action}:${crypto.randomUUID()}`;
    const params = {
      expectedLockVersion: detail.run.lockVersion,
      eventId: authorityKey,
      idempotencyKey: authorityKey,
      reason: actionReason(action),
    };

    setBusyAction(action);
    setError(null);
    try {
      const scope = { organizationId };
      if (action === "retry") await cmmdApiClient.retryForgeAutomationRun(runId, params, scope);
      if (action === "cancel") await cmmdApiClient.cancelForgeAutomationRun(runId, params, scope);
      if (action === "approve") {
        await cmmdApiClient.approveForgeAutomationEscalation(runId, params, scope);
      }
      if (action === "reject") {
        await cmmdApiClient.rejectForgeAutomationEscalation(runId, params, scope);
      }
      await refresh();
      onChanged();
    } catch (cause: unknown) {
      setError(sanitizePublicUiError(cause, `Unable to ${action} this automation run.`));
    } finally {
      setBusyAction(null);
    }
  }

  return {
    detail,
    loading,
    error,
    busyAction,
    refresh,
    retry: () => void perform("retry"),
    cancel: () => void perform("cancel"),
    approve: () => void perform("approve"),
    reject: () => void perform("reject"),
  };
}
